/**
 * Thin orchestration layer the UI calls to start, resume, and load a person's session.
 *
 * Port of lib/app_service.py. The Python version returns (user_path, run_path, user_profile,
 * run_data) tuples; the TS port keeps the same shape — paths are logical storage keys
 * (strings), profile/run are plain objects mutated/persisted via json_store over the storage
 * backend. These functions are synchronous (no AI/geocoder here; the intake form resolves the
 * place via the async geocoder BEFORE calling start_*).
 */

import type { PlainDate, PlainTime } from "./datetime";
import {
  append_log,
  build_user_profile,
  candidate_list_from_times,
  empty_run_data,
  ensure_user_profile_sections,
  load_or_create_run,
  load_times_state,
  read_json,
  save_state,
  times_path_from_user_path,
  unique_user_paths,
  user_paths,
} from "./json_store";
import { getStorage } from "./storage";
import { initial_intake_questions } from "./intake_questions";
import { ensure_questions } from "./rectification";
import { search_span_seconds, FINE_DIVISIONAL_MAX_SPAN_SECONDS } from "./scoring";
import type { Profile, RunData } from "../types";

export interface Session {
  user_path: string;
  run_path: string;
  user_profile: Profile;
  run_data: RunData;
}

function path_exists(path: string): boolean {
  return getStorage().read(path) !== null;
}

/**
 * Create a brand-new draft session. A NEW person must never silently target an existing
 * profile file: similar names sanitize to the same slug, so reserve a unique one and store
 * it on the profile as the identity for later loads. No user JSON is saved yet.
 */
export function start_draft_user_session(
  first_name: string,
  birth_date: PlainDate,
  lat: number,
  lon: number,
  certainty: string,
  entered_time: PlainTime | null,
  range_start: PlainTime | null,
  range_end: PlainTime | null,
  range_end_date: PlainDate | null = null,
  tz_offset_override: number | null = null,
): Session {
  const [user_path, run_path, slug] = unique_user_paths(first_name);
  const user_profile = build_user_profile(
    first_name,
    birth_date,
    lat,
    lon,
    certainty,
    entered_time,
    range_start,
    range_end,
    range_end_date,
    slug,
    tz_offset_override,
  );
  const run_data = empty_run_data(user_profile);
  append_log(run_data, "Created draft profile. No user JSON has been saved yet.");
  run_data["current_questions"] = initial_intake_questions(user_profile);
  run_data["stage"] = "answering_questions";
  append_log(run_data, "Prepared intake questions for the draft profile.");
  return { user_path, run_path, user_profile, run_data };
}

export function start_or_resume_user(
  first_name: string,
  birth_date: PlainDate,
  lat: number,
  lon: number,
  certainty: string,
  entered_time: PlainTime | null,
  range_start: PlainTime | null,
  range_end: PlainTime | null,
  range_end_date: PlainDate | null = null,
): Session {
  const [user_path, run_path] = user_paths(first_name);
  if (path_exists(user_path)) {
    const user_profile = read_json(user_path) as Profile;
    user_profile["_times"] = load_times_state(user_profile, times_path_from_user_path(user_path));
    const run_data = load_or_create_run(user_profile, run_path);
    if (ensure_user_profile_sections(user_profile, run_data)) {
      save_state(user_path, run_path, user_profile, run_data);
    }
    append_log(run_data, "Resumed existing user profile from intake.");
    save_state(user_path, run_path, user_profile, run_data);
    return { user_path, run_path, user_profile, run_data };
  }

  const user_profile = build_user_profile(
    first_name,
    birth_date,
    lat,
    lon,
    certainty,
    entered_time,
    range_start,
    range_end,
    range_end_date,
  );

  let run_data: RunData;
  if (path_exists(run_path)) {
    run_data = load_or_create_run(user_profile, run_path);
    append_log(run_data, "Resumed existing run from intake.");
  } else {
    run_data = empty_run_data(user_profile);
    append_log(run_data, "Saved intake profile.");
    run_data["current_questions"] = initial_intake_questions(user_profile);
    run_data["stage"] = "answering_questions";
    append_log(run_data, "Prepared intake questions.");
  }

  save_state(user_path, run_path, user_profile, run_data);
  return { user_path, run_path, user_profile, run_data };
}

export function load_user_session(active_user: string): Session {
  const [user_path, run_path] = user_paths(active_user);
  const user_profile = read_json(user_path) as Profile;
  user_profile["_times"] = load_times_state(user_profile, times_path_from_user_path(user_path));
  const run_data = load_or_create_run(user_profile, run_path);
  if (ensure_user_profile_sections(user_profile, run_data)) {
    save_state(user_path, run_path, user_profile, run_data);
  }
  const active = candidate_list_from_times(user_profile);
  const stage: string = run_data["stage"] ?? "";
  // Candidate scores are ephemeral (recomputed on open, never persisted to the cloud). If the
  // saved stage expects exact-time results but none exist, re-scan — but ONLY for a NARROW
  // window (Level 1: a committed sign or a tight window), where the per-second scan drives the
  // view. A WIDE window (Level 0) is driven by the live rising-sign breakdown, not stored times,
  // so leave it alone. `narrow` mirrors App.tsx's `wide` exactly so the two never disagree.
  const span = search_span_seconds(user_profile["search"] ?? {});
  const narrow = user_profile["rising_sign_locked"] === true || span <= FINE_DIVISIONAL_MAX_SPAN_SECONDS;
  if (!active.length && narrow && ["showing_internal_times", "showing_ai_review"].includes(stage)) {
    run_data["stage"] = "ready_internal_matching";
    // This re-scan only RESTORES the ephemeral scores after a reload — it is not a user-initiated
    // refinement pass, so it must not advance loop_count (which would silently contract the
    // refinement region on every app reopen with no new evidence). The one-shot flag rides on the
    // in-memory run_data to the worker dispatch (App.tsx passes no params); run_internal_matching
    // consumes it, and clean_run_data_for_save never persists it.
    run_data["suppress_loop_advance"] = true;
    save_state(user_path, run_path, user_profile, run_data);
  }
  if (active.length && !["showing_internal_times", "showing_ai_review"].includes(stage)) {
    run_data["stage"] = active.some((item: any) => item["ai_score"] != null)
      ? "showing_ai_review"
      : "showing_internal_times";
    save_state(user_path, run_path, user_profile, run_data);
  }
  if (
    (user_profile["events"]?.length ?? 0) > 0 &&
    !active.length &&
    !["ready_internal_matching", "showing_internal_times", "showing_ai_review"].includes(stage)
  ) {
    run_data["stage"] = "ready_internal_matching";
    save_state(user_path, run_path, user_profile, run_data);
  }
  if (
    !(user_profile["events"]?.length ?? 0) &&
    !(run_data["current_questions"]?.length ?? 0) &&
    !active.length
  ) {
    ensure_questions(user_profile, run_data);
    save_state(user_path, run_path, user_profile, run_data);
  }
  return { user_path, run_path, user_profile, run_data };
}

/** List saved people (slug + first name) for the resume picker. Mirrors render_intake's glob. */
export function list_saved_people(): { slug: string; first_name: string; has_events: boolean }[] {
  const storage = getStorage();
  const out: { slug: string; first_name: string; has_events: boolean }[] = [];
  for (const path of storage.list("users/")) {
    const name = path.slice("users/".length);
    if (name.endsWith("_run.json") || name.endsWith("_times.json")) continue;
    if (name.startsWith("_")) continue; // _draft.json, _geocode_cache.json
    if (!name.endsWith(".json")) continue;
    const slug = name.slice(0, -".json".length);
    try {
      const profile = read_json(path);
      out.push({
        slug,
        first_name: String(profile["first_name"] || slug),
        has_events: (profile["events"]?.length ?? 0) > 0,
      });
    } catch {
      // skip unreadable
    }
  }
  return out.sort((a, b) => a.first_name.localeCompare(b.first_name));
}
